import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { typography } from '../../styles/typography';
import { Card, PrimaryButton } from '../ui';
import { LOST_FOUND_STATUS } from './lostAndFoundData';
import { getLostFoundSiteId, lostAndFoundApi } from './lostAndFoundApi';
import LostAndFoundImageUpload from './LostAndFoundImageUpload';

const INPUT_CLASS =
  'w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-base text-black font-["Open_Sans"] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6BABA0]/40 focus:border-[#6BABA0]';

const LABEL_CLASS = 'block text-sm font-medium text-black mb-2 font-["Open_Sans"]';

const Field = ({ label, children }) => (
  <div>
    <label className={LABEL_CLASS}>{label}</label>
    {children}
  </div>
);

const emptyForm = {
  item_name: '',
  status: LOST_FOUND_STATUS.LOST,
  lost_found_at: '',
  submit_collect_at: '',
  description: '',
};

const CreateLostAndFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id) && location.pathname.endsWith('/edit');
  const siteId = getLostFoundSiteId();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const isFoundStatus = form.status === LOST_FOUND_STATUS.FOUND;
  const locationLabel = isFoundStatus ? 'Found at' : 'Lost at';
  const handoffLabel = isFoundStatus ? 'Collect at' : 'Submit at';
  const locationPlaceholder = isFoundStatus
    ? 'Where was the item found?'
    : 'Where was the item lost?';
  const handoffPlaceholder = isFoundStatus
    ? 'Where can the owner collect the item?'
    : 'Where should the item be submitted?';

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (!isEditMode || !id) return;

    const loadItem = async () => {
      setIsLoading(true);
      setError('');
      try {
        const item = await lostAndFoundApi.getById(id, siteId);
        if (item.status === LOST_FOUND_STATUS.COLLECTED) {
          navigate(`/lost-and-found/${id}`, { replace: true });
          return;
        }
        setForm({
          item_name: item.item_name || '',
          status: item.status || LOST_FOUND_STATUS.LOST,
          lost_found_at: item.location || '',
          submit_collect_at: item.submit_collect_at || '',
          description: item.description || '',
        });
        if (item.image_url) {
          setImagePreview(item.image_url);
        }
      } catch (err) {
        setError(err.message || 'Failed to load item');
      } finally {
        setIsLoading(false);
      }
    };

    loadItem();
  }, [isEditMode, id, siteId, navigate]);

  const handleImageChange = dataUrl => {
    setImagePreview(dataUrl);
  };

  const uploadImageIfNeeded = async itemId => {
    if (!imageFile) return null;
    return lostAndFoundApi.uploadItemImage(itemId, imageFile, siteId);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!siteId) {
      setError('Select a site on the Lost & Found list before creating items.');
      return;
    }
    if (!form.item_name.trim()) {
      setError('Item name is required');
      return;
    }
    if (!form.lost_found_at.trim()) {
      setError(`${locationLabel} is required`);
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const payload = {
        item_name: form.item_name,
        status: form.status,
        location: form.lost_found_at,
        submit_collect_at: form.submit_collect_at,
        description: form.description,
      };

      if (isEditMode) {
        const imageKey = await uploadImageIfNeeded(id);
        if (imageKey) payload.image_s3_key = imageKey;
        await lostAndFoundApi.update(id, payload, siteId);
        navigate(`/lost-and-found/${id}`);
      } else {
        const created = await lostAndFoundApi.create(payload, siteId);
        const itemId = created?.id;
        if (!itemId) throw new Error('Item was created but no id was returned');
        const imageKey = await uploadImageIfNeeded(itemId);
        if (imageKey) {
          await lostAndFoundApi.update(itemId, { image_s3_key: imageKey }, siteId);
        }
        navigate(`/lost-and-found/${itemId}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to save item');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !id) return;
    const confirmed = window.confirm(
      `Delete "${form.item_name || 'this item'}"? This cannot be undone.`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    setError('');
    try {
      await lostAndFoundApi.delete(id, siteId);
      navigate('/lost-and-found');
    } catch (err) {
      setError(err.message || 'Failed to delete item');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!siteId && !isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-amber-700 font-['Open_Sans']">
          Select a site on the Lost & Found list first.
        </p>
        <button
          type="button"
          onClick={() => navigate('/lost-and-found')}
          className="px-4 py-2 rounded-lg bg-[#6BABA0] text-white"
        >
          Go to list
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-[#6BABA0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-6">
      <h3 className={typography.h3} style={{ color: '#000000' }}>
        {isEditMode ? 'Edit Lost & Found Item' : 'Create Lost & Found Item'}
      </h3>

      <Card className="p-6 md:p-8">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div className="flex flex-col gap-4">
              <Field label="Item name">
                <input
                  type="text"
                  value={form.item_name}
                  onChange={e => updateField('item_name', e.target.value)}
                  placeholder="Enter item name"
                  className={INPUT_CLASS}
                />
              </Field>

              <Field label="Report type">
                <select
                  value={form.status}
                  onChange={e => updateField('status', e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value={LOST_FOUND_STATUS.LOST}>Lost</option>
                  <option value={LOST_FOUND_STATUS.FOUND}>Found</option>
                </select>
              </Field>

              <Field label={locationLabel}>
                <input
                  type="text"
                  value={form.lost_found_at}
                  onChange={e => updateField('lost_found_at', e.target.value)}
                  placeholder={locationPlaceholder}
                  className={INPUT_CLASS}
                />
              </Field>

              <Field label={handoffLabel}>
                <input
                  type="text"
                  value={form.submit_collect_at}
                  onChange={e => updateField('submit_collect_at', e.target.value)}
                  placeholder={handoffPlaceholder}
                  className={INPUT_CLASS}
                />
              </Field>

              <Field label="Description">
                <textarea
                  value={form.description}
                  onChange={e => updateField('description', e.target.value)}
                  placeholder="Describe the item"
                  rows={4}
                  className={`${INPUT_CLASS} resize-none`}
                />
              </Field>
            </div>

            <LostAndFoundImageUpload
              label="Item image"
              value={imagePreview}
              onChange={handleImageChange}
              onFileSelect={setImageFile}
              onValidationError={setError}
              emptyTitle="Upload item photo"
              emptyHint="Drag and drop here, or browse from your device"
              className="w-full lg:sticky lg:top-4"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 font-['Open_Sans']">{error}</p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-zinc-100">
            {isEditMode ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting || isSaving}
                className="px-5 py-2.5 rounded-xl border border-red-300 text-red-600 text-sm font-medium font-['Open_Sans'] hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            ) : (
              <span />
            )}
            <div className="flex flex-wrap gap-3 ml-auto">
              <button
                type="button"
                onClick={() =>
                  navigate(isEditMode ? `/lost-and-found/${id}` : '/lost-and-found')
                }
                className="px-6 py-3 rounded-xl border border-zinc-300 text-black font-['Open_Sans'] hover:bg-gray-50 transition-colors min-w-[120px]"
              >
                Cancel
              </button>
              <PrimaryButton
                type="submit"
                disabled={isSaving || isDeleting}
                className="!w-auto min-w-[120px] !h-12 !text-base"
              >
                {isSaving
                  ? isEditMode
                    ? 'Updating...'
                    : 'Submitting...'
                  : isEditMode
                    ? 'Update'
                    : 'Submit'}
              </PrimaryButton>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateLostAndFound;
